import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { resolveEntitySlug } from "@/lib/slug";
import { uploadPublicImage } from "@/lib/upload";
import { usePortalUserContext } from "@/admin/PortalUserContext";
import { SocialLinksFormFields } from "@/components/admin/SocialLinksFormFields";
import { AdminImagePicker } from "@/components/admin/AdminImagePicker";
import { adminListRowHighlightClass } from "@/lib/adminListRowHighlight";
import {
  adminAddNewSecondaryButtonClass,
  adminFormSectionClass,
  adminListSectionClass,
  adminScopedFormSectionClass,
} from "@/lib/adminSectionChrome";
import { socialUrlOrNull } from "@/lib/entitySocialLinks";
import { portalHasSiteWideWrite } from "@/lib/portalSession";

type Row = {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  logo_url: string | null;
  exco_photo_url: string | null;
  social_facebook_url: string | null;
  social_instagram_url: string | null;
  social_youtube_url: string | null;
  social_website_url: string | null;
  social_other_url: string | null;
};

export function AssociationsTab() {
  const portal = usePortalUserContext();
  const siteWide = portal ? portalHasSiteWideWrite(portal.grants) : false;
  const assocWorkspaces = useMemo(
    () => portal?.assignments.filter((a) => a.kind === "association") ?? [],
    [portal?.assignments]
  );
  const activeAssocWorkspace = useMemo(() => {
    if (!portal || assocWorkspaces.length === 0) return null;
    const hit = assocWorkspaces.find((a) => a.id === portal.activeAssignmentId);
    return hit ?? assocWorkspaces[0];
  }, [portal, assocWorkspaces]);
  const scopeAssocId = activeAssocWorkspace?.parent_id ?? null;

  const [rows, setRows] = useState<Row[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [excoPhotoUrl, setExcoPhotoUrl] = useState("");
  const [excoFile, setExcoFile] = useState<File | null>(null);
  const [usernameUrl, setUsernameUrl] = useState("");
  const [socialFacebook, setSocialFacebook] = useState("");
  const [socialInstagram, setSocialInstagram] = useState("");
  const [socialYoutube, setSocialYoutube] = useState("");
  const [socialWebsite, setSocialWebsite] = useState("");
  const [socialOther, setSocialOther] = useState("");
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    let q = supabase.from("associations").select("*");
    if (scopeAssocId) q = q.eq("id", scopeAssocId);
    const { data } = await q.order("name");
    setRows((data as Row[]) ?? []);
  }, [scopeAssocId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (siteWide || rows.length !== 1) return;
    startEdit(rows[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteWide, rows]);

  function startNew() {
    setEditingId(null);
    setName("");
    setDescription("");
    setLogoUrl("");
    setLogoFile(null);
    setExcoPhotoUrl("");
    setExcoFile(null);
    setUsernameUrl("");
    setSocialFacebook("");
    setSocialInstagram("");
    setSocialYoutube("");
    setSocialWebsite("");
    setSocialOther("");
    setMsg("");
  }

  function startEdit(r: Row) {
    setEditingId(r.id);
    setName(r.name);
    setDescription(r.description ?? "");
    setLogoUrl(r.logo_url ?? "");
    setLogoFile(null);
    setExcoPhotoUrl(r.exco_photo_url ?? "");
    setExcoFile(null);
    setUsernameUrl(r.slug);
    setSocialFacebook(r.social_facebook_url ?? "");
    setSocialInstagram(r.social_instagram_url ?? "");
    setSocialYoutube(r.social_youtube_url ?? "");
    setSocialWebsite(r.social_website_url ?? "");
    setSocialOther(r.social_other_url ?? "");
    setMsg("");
  }

  async function onSave(e: FormEvent) {
    e.preventDefault();
    setMsg("");
    try {
      let nextLogo = logoUrl.trim();
      if (logoFile) nextLogo = await uploadPublicImage(logoFile, "associations");
      let nextExco = excoPhotoUrl.trim();
      if (excoFile) nextExco = await uploadPublicImage(excoFile, "associations");
      const nameTrim = name.trim();
      if (!nameTrim) {
        setMsg("Name is required.");
        return;
      }
      const finalSlug = resolveEntitySlug(usernameUrl, nameTrim, "association");
      const payload = {
        slug: finalSlug,
        name: nameTrim,
        description: description || null,
        logo_url: nextLogo || null,
        exco_photo_url: nextExco || null,
        social_facebook_url: socialUrlOrNull(socialFacebook),
        social_instagram_url: socialUrlOrNull(socialInstagram),
        social_youtube_url: socialUrlOrNull(socialYoutube),
        social_website_url: socialUrlOrNull(socialWebsite),
        social_other_url: socialUrlOrNull(socialOther),
      };
      if (editingId) {
        const { error } = await supabase.from("associations").update(payload).eq("id", editingId);
        if (error) throw error;
        setMsg("Updated.");
      } else {
        if (!siteWide) {
          setMsg("Only full admins can create associations.");
          return;
        }
        const { error } = await supabase.from("associations").insert(payload);
        if (error) throw error;
        setMsg("Created.");
      }
      setLogoFile(null);
      setExcoFile(null);
      void load();
      startNew();
    } catch (err) {
      console.error(err);
      setMsg("Error saving (check Username / URL is unique).");
    }
  }

  async function onDelete(id: string) {
    if (!siteWide) return;
    if (!confirm("Delete association and its projects?")) return;
    const { error } = await supabase.from("associations").delete().eq("id", id);
    if (error) console.error(error);
    void load();
    if (editingId === id) startNew();
  }

  const associationForm = (
    <form onSubmit={onSave} className="mt-5 space-y-4">
      {scopeAssocId && !siteWide ? (
        <p className="text-xs text-brand-blue/70">
          You can update this association&apos;s page. Creating or deleting associations requires a full admin.
        </p>
      ) : null}
        <div className="space-y-1">
          <label htmlFor="assoc-name" className="block text-xs font-semibold text-brand-blue">
            Name
          </label>
          <input
            id="assoc-name"
            required
            autoComplete="off"
            placeholder="Association name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded border border-brand-gray px-3 py-2 text-sm"
          />
        </div>
        <div className="space-y-1">
          <label htmlFor="assoc-description" className="block text-xs font-semibold text-brand-blue">
            Description
          </label>
          <textarea
            id="assoc-description"
            placeholder="About this association"
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full rounded border border-brand-gray px-3 py-2 text-sm"
          />
        </div>
        <AdminImagePicker
          label="Logo"
          inputId="assoc-logo"
          hint={
            <>
              Shown on the associations directory and at the top of the association page (use a square
              or wide mark; SVG is allowed if under the size limit).
            </>
          }
          remoteUrl={logoUrl}
          selectedFile={logoFile}
          onFileChange={setLogoFile}
          onClear={() => {
            setLogoFile(null);
            setLogoUrl("");
          }}
          imgClassName="h-20 w-20 rounded border border-brand-gray/60 bg-white object-contain p-1"
        />
        <AdminImagePicker
          label="ExCo photo"
          inputId="assoc-photo"
          remoteUrl={excoPhotoUrl}
          selectedFile={excoFile}
          onFileChange={setExcoFile}
          onClear={() => {
            setExcoFile(null);
            setExcoPhotoUrl("");
          }}
        />
        <SocialLinksFormFields
          fieldIdPrefix="assoc"
          facebookUrl={socialFacebook}
          instagramUrl={socialInstagram}
          youtubeUrl={socialYoutube}
          websiteUrl={socialWebsite}
          otherUrl={socialOther}
          onFacebookUrl={setSocialFacebook}
          onInstagramUrl={setSocialInstagram}
          onYoutubeUrl={setSocialYoutube}
          onWebsiteUrl={setSocialWebsite}
          onOtherUrl={setSocialOther}
        />
        <div className="space-y-1">
          <label htmlFor="assoc-username-url" className="block text-xs font-semibold text-brand-blue">
            Username / URL <span className="font-normal text-brand-blue/60">(optional)</span>
          </label>
          <input
            id="assoc-username-url"
            autoComplete="off"
            placeholder="Leave blank to auto-generate from name"
            value={usernameUrl}
            onChange={(e) => setUsernameUrl(e.target.value)}
            className="w-full rounded border border-brand-gray px-3 py-2 text-sm"
          />
          <p className="text-xs text-brand-blue/60">
            Public URL segment under <span className="font-mono">/alumni/associations/</span>. When
            editing, the field shows the current value; clear it to derive again from the name.
          </p>
        </div>
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={!editingId && !siteWide}
          className="rounded-full bg-brand-maroon px-6 py-2 text-sm font-bold uppercase text-white disabled:opacity-50"
        >
          Save
        </button>
        <button
          type="button"
          onClick={startNew}
          className="rounded-full border border-brand-gray px-4 py-2 text-sm"
        >
          Clear
        </button>
      </div>
      {msg && <p className="text-sm">{msg}</p>}
    </form>
  );

  return (
    <div>
      <h2 className="font-display text-2xl font-bold text-brand-blue">Associations</h2>

      {siteWide ? (
        <div className="mt-6 flex flex-col gap-6 lg:flex-row lg:items-start">
          <section aria-labelledby="associations-list-heading" className={adminListSectionClass}>
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-brand-gray pb-3">
              <h3 id="associations-list-heading" className="font-display text-lg font-bold text-brand-blue">
                Association list
              </h3>
              <button
                type="button"
                onClick={() => {
                  if (editingId) startNew();
                }}
                disabled={!editingId}
                title={
                  !editingId
                    ? "You are already adding a new association — use the form on the right"
                    : "Clear the editor and add a new association"
                }
                className={adminAddNewSecondaryButtonClass}
              >
                Add new association
              </button>
            </div>
            <div className="mt-4 max-h-[min(70vh,36rem)] overflow-auto rounded-lg border border-brand-gray/80 bg-brand-gray/30 p-2">
              <ul className="space-y-1">
                {rows.map((r) => {
                  const selected = editingId === r.id;
                  return (
                    <li
                      key={r.id}
                      className={`flex items-center justify-between gap-2 rounded-lg px-3 py-3 transition-colors ${adminListRowHighlightClass(selected)}`}
                    >
                      <span className="min-w-0 flex-1 font-medium text-brand-blue">{r.name}</span>
                      <div className="flex shrink-0 flex-row items-center gap-4">
                        <button
                          type="button"
                          className="text-xs font-bold uppercase tracking-wide text-brand-maroon"
                          onClick={() => startEdit(r)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="text-xs font-bold uppercase tracking-wide text-red-700"
                          onClick={() => void onDelete(r.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </section>
          <section aria-labelledby="associations-form-heading" className={adminFormSectionClass}>
            <div className="border-b border-brand-gray pb-3">
              <h3 id="associations-form-heading" className="font-display text-lg font-bold text-brand-blue">
                {editingId ? "Edit association" : "Add new association"}
              </h3>
              {editingId && name.trim() ? (
                <p className="mt-1 text-sm text-brand-blue/65 line-clamp-2">&ldquo;{name.trim()}&rdquo;</p>
              ) : null}
            </div>
            {associationForm}
          </section>
        </div>
      ) : (
        <>
          <section aria-labelledby="associations-form-heading" className={adminScopedFormSectionClass}>
            <div className="border-b border-brand-gray pb-3">
              <h3 id="associations-form-heading" className="font-display text-lg font-bold text-brand-blue">
                {scopeAssocId ? "Your association" : editingId ? "Edit association" : "Add new association"}
              </h3>
              {editingId && name.trim() ? (
                <p className="mt-1 text-sm text-brand-blue/65 line-clamp-2">&ldquo;{name.trim()}&rdquo;</p>
              ) : null}
            </div>
            {associationForm}
          </section>
          {scopeAssocId && rows[0] ? (
            <p className="mt-6 text-sm text-brand-blue/80">
              Use the form above to save changes to <strong>{rows[0].name}</strong>.
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}
